import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './utils';

/**
 * Calendar Component - MEBI Design System
 * 
 * Pure calendar grid component with navigation
 * Following Figma specs with MEBI tokens
 */

export interface CalendarProps {
  /**
   * Currently selected date
   */
  selectedDate?: Date | null;
  
  /**
   * Current viewing month (0-11)
   */
  currentMonth: number;
  
  /**
   * Current viewing year
   */
  currentYear: number;
  
  /**
   * Callback when date is selected
   */
  onDateSelect: (date: Date) => void;
  
  /**
   * Callback when month/year navigation is clicked
   */
  onNavigate: (direction: 'prev' | 'next') => void;
  
  /**
   * Callback when header (month/year) is clicked
   */
  onHeaderClick?: () => void;
  
  /**
   * Additional className for calendar container
   */
  className?: string;
  
  /**
   * Whether header is clickable for month/year picker
   */
  headerClickable?: boolean;
}

const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  ({ 
    selectedDate,
    currentMonth,
    currentYear,
    onDateSelect,
    onNavigate,
    onHeaderClick,
    className,
    headerClickable = false,
  }, ref) => {
    const monthNames = [
      'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
      'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12',
    ];

    const getDaysInMonth = (month: number, year: number) =>
      new Date(year, month + 1, 0).getDate();
    
    const getFirstDayOfMonth = (month: number, year: number) =>
      (new Date(year, month, 1).getDay() + 6) % 7; // Convert to Monday = 0

    const generateCalendarDays = () => {
      const daysInMonth = getDaysInMonth(currentMonth, currentYear);
      const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
      const days = [];
      
      // Empty cells for days before first day of month
      for (let i = 0; i < firstDay; i++) days.push(null);
      
      // Days of the month
      for (let day = 1; day <= daysInMonth; day++) days.push(day);
      
      return days;
    };

    const isSelectedDate = (day: number | null) => {
      if (!day || !selectedDate) return false;
      return (
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === currentMonth &&
        selectedDate.getFullYear() === currentYear
      );
    };

    const handleDateClick = (day: number | null) => {
      if (!day) return;
      const newDate = new Date(currentYear, currentMonth, day);
      onDateSelect(newDate);
    };

    const calendarDays = generateCalendarDays();

    return (
      <div ref={ref} className={cn("flex flex-col gap-4", className)}>
        {/* Header */}
        <div className="flex items-center justify-between w-full">
          <button
            onClick={() => onNavigate('prev')}
            className="flex items-center justify-center w-4 h-4 transition-colors duration-200 hover:opacity-70"
            type="button"
          >
            <ChevronLeft className="w-4 h-4 text-neutral-600" />
          </button>
          
          <button
            onClick={headerClickable ? onHeaderClick : undefined}
            disabled={!headerClickable}
            className={cn(
              "mebi-body-small-semibold text-neutral-1300 text-center w-[124px] bg-transparent border-none",
              headerClickable ? "cursor-pointer transition-colors duration-200 hover:opacity-70" : "cursor-default"
            )}
            type="button"
          >
            {monthNames[currentMonth]} {currentYear}
          </button>
          
          <button
            onClick={() => onNavigate('next')}
            className="flex items-center justify-center w-4 h-4 transition-colors duration-200 hover:opacity-70"
            type="button"
          >
            <ChevronRight className="w-4 h-4 text-neutral-600" />
          </button>
        </div>

        {/* Weekdays */}
        <div className="grid grid-cols-7 gap-2">
          {['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map((day) => (
            <div
              key={day}
              className="flex items-center justify-center w-[30px] h-[30px] mebi-body-xsmall-bold text-mebi-yellow-500 uppercase"
            >
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((day, index) => (
            <button
              key={index}
              onClick={() => handleDateClick(day)}
              disabled={!day}
              type="button"
              className={cn(
                "flex items-center justify-center w-[30px] h-[30px] rounded-[29px] mebi-body-xsmall-regular transition-colors duration-200",
                isSelectedDate(day)
                  ? "bg-mebi-yellow-500 text-white"
                  : day
                  ? "hover:bg-mebi-yellow-200 text-neutral-1300"
                  : "text-transparent cursor-default"
              )}
            >
              {day}
            </button>
          ))}
        </div>
      </div>
    );
  }
);

Calendar.displayName = 'Calendar';

export { Calendar };
